<script lang="ts">
  import { createEventDispatcher } from 'svelte';

  // Default Prop
  export let id: string | undefined;
  export let minName: string | undefined;
  export let maxName: string | undefined;
  export let minText: string | undefined;
  export let maxText: string | undefined;
  export let min: number = 0;
  export let max: number = Number.MAX_SAFE_INTEGER;
  export let step: number = 1;
  export let disabled = false;
  export let formatter: ((v: number) => string) | undefined;

  let rangeMinInput: HTMLInputElement;
  let rangeMaxInput: HTMLInputElement;

  export let currentMinValue: number = min;
  export let currentMaxValue: number = max;

  const eventDispatcher = createEventDispatcher();

  $: {
    if (rangeMinInput) {
      rangeMinInput.valueAsNumber = currentMinValue;
    }
    if (rangeMaxInput) {
      rangeMaxInput.valueAsNumber = currentMaxValue;
    }
  }

  const onMinChange = () => {
    if (rangeMinInput.valueAsNumber >= rangeMaxInput.valueAsNumber) {
      rangeMaxInput.valueAsNumber = rangeMinInput.valueAsNumber + step;

      if (rangeMaxInput.valueAsNumber >= max) {
        rangeMaxInput.valueAsNumber = max;
        rangeMinInput.valueAsNumber = max - step;
      }
    }
    currentMinValue = rangeMinInput.valueAsNumber;
    currentMaxValue = rangeMaxInput.valueAsNumber;
  };

  const onMaxChange = () => {
    if (rangeMaxInput.valueAsNumber <= rangeMinInput.valueAsNumber) {
      rangeMinInput.valueAsNumber = rangeMaxInput.valueAsNumber - step;

      if (rangeMinInput.valueAsNumber <= min) {
        rangeMinInput.valueAsNumber = min;
        rangeMaxInput.valueAsNumber = min + step;
      }
    }
    currentMinValue = rangeMinInput.valueAsNumber;
    currentMaxValue = rangeMaxInput.valueAsNumber;
  };
</script>

<fieldset class="range-control">
  <div class="range-inputs">
    <input
      class="range-input"
      type="range"
      name={minName}
      id="{id}-min"
      {min}
      {max}
      {step}
      value={min}
      {disabled}
      on:click
      on:input={onMinChange}
      bind:this={rangeMinInput}
    />
    <input
      class="range-input"
      type="range"
      name={maxName}
      id="{id}-max"
      {min}
      {max}
      {step}
      value={max}
      {disabled}
      on:click
      on:input={onMaxChange}
      bind:this={rangeMaxInput}
    />
  </div>

  <div class="range-labels">
    <label class="range-label" for="{id}-min">
      <span>{minText}</span>
      <span class="range-label-value">
        {formatter ? formatter(currentMinValue) : currentMinValue}
      </span>
    </label>
    <label class="range-label" for="{id}-max">
      <span>{maxText}</span>
      <span class="range-label-value">
        {formatter ? formatter(currentMaxValue) : currentMaxValue}
      </span>
    </label>
  </div>
</fieldset>

<style type="text/scss" lang="scss" scoped>
  .range-inputs {
    @apply relative pt-5;
  }

  .range-input {
    @apply absolute inset-x-0 top-0;
    @apply appearance-none;
    @apply w-full;
    @apply bg-transparent;
    @apply pointer-events-none;
  }

  .range-input::-webkit-slider-runnable-track {
    @apply w-full h-[3px];
    @apply bg-gray-600 bg-opacity-50;
    @apply cursor-pointer;
    @apply pointer-events-auto;
  }
  .range-input::-moz-range-track {
    @apply w-full h-[3px];
    @apply bg-gray-600 bg-opacity-50;
    @apply cursor-pointer;
    @apply pointer-events-auto;
  }
  .range-input::-ms-track {
    @apply w-full h-[3px];
    @apply bg-transparent;
    @apply cursor-pointer;
    @apply pointer-events-auto;
  }
  .range-input::-ms-fill-lower {
    @apply bg-gray-600 bg-opacity-50;
    @apply border-0;
    @apply pointer-events-auto;
  }
  .range-input::-ms-fill-upper {
    @apply bg-gray-600 bg-opacity-50;
    @apply border-0;
    @apply pointer-events-auto;
  }

  .range-input::-webkit-slider-thumb {
    @apply appearance-none shadow-none border-0;
    @apply w-5 h-5 rounded-full;
    @apply bg-black;
    @apply cursor-pointer;
    @apply -mt-2;
    @apply pointer-events-auto;
  }
  .range-input::-moz-range-thumb {
    @apply appearance-none shadow-none border-0;
    @apply w-5 h-5 rounded-full;
    @apply bg-black;
    @apply cursor-pointer;
    @apply pointer-events-auto;
  }
  .range-input::-ms-thumb {
    @apply appearance-none shadow-none border-0;
    @apply w-5 h-5 rounded-full;
    @apply bg-black;
    @apply cursor-pointer;
    @apply pointer-events-auto;
    @apply z-10;
  }

  .range-input:last-child::-webkit-slider-runnable-track {
    @apply h-0;
  }
  .range-input:last-child::-moz-range-track {
    @apply h-0 pointer-events-none;
  }
  .range-input:last-child::-ms-track {
    @apply h-0;
  }

  .range-labels {
    @apply mt-4 pt-4;
    @apply border-t border-gray-600 border-opacity-10;
    @apply flex items-start justify-between;
    @apply text-sm font-semibold tracking-wider uppercase;
    @apply text-black;
  }

  .range-label {
    @apply flex flex-col;
  }

  .range-label-value {
    @apply font-normal normal-case tracking-normal;
    @apply mt-1;
  }
</style>
